import Component from "@glimmer/component";
import { tracked } from "@glimmer/tracking";
import { action } from "@ember/object";
import { service } from "@ember/service";
import { trustHTML } from "@ember/template";
import { isEmpty } from "@ember/utils";
import { popupAjaxError } from "discourse/lib/ajax-error";
import discourseLater from "discourse/lib/later";
import DButton from "discourse/ui-kit/d-button";
import DModal from "discourse/ui-kit/d-modal";
import { i18n } from "discourse-i18n";
import {
  EXISTING_TOPIC_SELECTION,
  NEW_TOPIC_SELECTION,
} from "discourse/plugins/chat/discourse/components/chat-to-topic-selector";
import { CHANNEL_STATUSES } from "discourse/plugins/chat/discourse/models/chat-channel";
import ChatToTopicSelector from "../../chat-to-topic-selector";

export default class ChatModalArchiveChannel extends Component {
  @service chatApi;
  @service siteSettings;

  @tracked selection = NEW_TOPIC_SELECTION;
  @tracked saving = false;
  @tracked topicTitle = null;
  @tracked categoryId = null;
  @tracked tags = null;
  @tracked selectedTopicId = null;
  @tracked flash;
  @tracked flashType;

  get channel() {
    return this.args.model.channel;
  }

  get newTopic() {
    return this.selection === NEW_TOPIC_SELECTION;
  }

  get existingTopic() {
    return this.selection === EXISTING_TOPIC_SELECTION;
  }

  get buttonDisabled() {
    if (this.saving) {
      return true;
    }

    if (
      this.newTopic &&
      (!this.topicTitle ||
        this.topicTitle.length < this.siteSettings.min_topic_title_length ||
        this.topicTitle.length > this.siteSettings.max_topic_title_length)
    ) {
      return true;
    }

    if (this.existingTopic && isEmpty(this.selectedTopicId)) {
      return true;
    }

    return false;
  }

  get instructionLabels() {
    const labels = {};
    labels[NEW_TOPIC_SELECTION] = i18n(
      "chat.selection.new_topic.instructions_channel_archive"
    );
    labels[EXISTING_TOPIC_SELECTION] = i18n(
      "chat.selection.existing_topic.instructions_channel_archive"
    );
    return labels;
  }

  get instructionsText() {
    return trustHTML(
      i18n("chat.channel_archive.instructions", {
        channelTitle: this.channel.escapedTitle,
      })
    );
  }

  get data() {
    const data = { type: this.selection };
    if (this.newTopic) {
      data.title = this.topicTitle;
      data.category_id = this.categoryId;
      data.tags = (this.tags ?? []).map((tag) =>
        typeof tag === "string" ? tag : tag.name
      );
    }
    if (this.existingTopic) {
      data.topic_id = this.selectedTopicId;
    }
    return data;
  }

  @action
  archiveChannel() {
    this.saving = true;

    return this.chatApi
      .createChannelArchive(this.channel.id, this.data)
      .then(() => {
        this.flash = i18n("chat.channel_archive.process_started");
        this.flashType = "success";
        this.channel.status = CHANNEL_STATUSES.archived;

        discourseLater(() => {
          this.args.closeModal();
        }, 3000);
      })
      .catch(popupAjaxError)
      .finally(() => (this.saving = false));
  }

  @action
  newTopicSelected(topic) {
    this.selectedTopicId = topic?.id;
  }

  <template>
    <DModal
      class="chat-modal-archive-channel"
      @closeModal={{@closeModal}}
      @flash={{this.flash}}
      @flashType={{this.flashType}}
      @inline={{@inline}}
      @title={{i18n "chat.channel_archive.title"}}
    >
      <:body>
        <p class="chat-modal-archive-channel__instructions">
          {{this.instructionsText}}
        </p>
        <ChatToTopicSelector
          @allowNewMessage={{false}}
          @categoryId={{this.categoryId}}
          @instructionLabels={{this.instructionLabels}}
          @selectedTopicId={{this.selectedTopicId}}
          @selection={{this.selection}}
          @tags={{this.tags}}
          @topicChangedCallback={{this.newTopicSelected}}
          @topicTitle={{this.topicTitle}}
        />
      </:body>
      <:footer>
        <DButton
          class="btn-primary"
          id="chat-confirm-archive-channel"
          @action={{this.archiveChannel}}
          @disabled={{this.buttonDisabled}}
          @label="chat.channel_archive.title"
        />
      </:footer>
    </DModal>
  </template>
}
